import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUpload({ value, onChange, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value && !value.startsWith('/api/files/') ? 'url' : 'upload');
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = `${API}/files/${data.path}`;
      onChange(imageUrl);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    onChange('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div data-testid="image-upload">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`text-xs px-2 py-0.5 rounded ${mode === 'upload' ? 'bg-[#0A0A0A] text-[#D4AF37]' : 'bg-gray-100 text-gray-500'}`}
            data-testid="mode-upload"
          >
            Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`text-xs px-2 py-0.5 rounded ${mode === 'url' ? 'bg-[#0A0A0A] text-[#D4AF37]' : 'bg-gray-100 text-gray-500'}`}
            data-testid="mode-url"
          >
            URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <div
            className="border-2 border-dashed border-gray-200 rounded-sm p-4 text-center cursor-pointer hover:border-[#D4AF37] transition-colors"
            onClick={() => !uploading && fileRef.current?.click()}
            data-testid="upload-dropzone"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-gray-400">
                <Upload size={20} />
                <span className="text-sm">Click to upload image</span>
                <span className="text-xs">PNG, JPG, WebP up to 10MB</span>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      ) : (
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
          data-testid="image-url-input"
        />
      )}

      {value && (
        <div className="mt-2 relative inline-block">
          <img
            src={value}
            alt="Preview"
            className="h-20 w-20 object-cover rounded-sm border border-gray-200"
            onError={e => { e.target.style.display = 'none'; }}
            data-testid="image-preview"
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
            data-testid="clear-image"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
